import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

/**
 * Centralised SEO component to keep meta tags consistent across routes.
 */
export default function Seo({
  title = 'YAQAM',
  description = "Planifiez vos repas, découvrez des recettes personnalisées et gérez vos courses facilement avec YAQAM.",
  image = '/img/idees_repas_personnalisees.png',
  canonical,
  type = 'website',
  noindex = false,
  siteName = 'YAQAM',
  locale = 'fr_FR',
  keywords,
  organization = false,
  logo = '/img/idees_repas_personnalisees.png',
  breadcrumb = false,
  jsonLd = []
}) {
  const location = useLocation();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const url = canonical || `${origin}${location.pathname}`;
  const absImage = image?.startsWith('http') ? image : `${origin}${image}`;
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;

  const schemas = [];

  if (organization) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteName,
      url,
      logo: logo?.startsWith('http') ? logo : `${origin}${logo}`
    });
  }

  if (breadcrumb) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: siteName, item: origin || '/' },
        { '@type': 'ListItem', position: 2, name: title, item: url }
      ]
    });
  }

  const providedSchemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  providedSchemas.filter(Boolean).forEach((schema) => schemas.push(schema));

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={Array.isArray(keywords) ? keywords.join(', ') : keywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={absImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
